<template>
  <div class="min-h-screen bg-white">
    <!-- Header with Navigation -->
    <div class="flex justify-between items-center w-full bg-white px-6 py-4 border-b">
      <div class="flex items-center space-x-6">
        <h1 class="text-2xl font-bold text-black">Admin Dashboard</h1>
        <nav class="flex space-x-4">
          <router-link 
            to="/admin/dashboard" 
            class="text-black hover:text-blue-600"
            :class="{ 'text-blue-600 font-semibold': $route.path === '/admin/dashboard' }"
          >
            Dashboard
          </router-link>
          <router-link 
            to="/admin/change-password" 
            class="text-black hover:text-blue-600"
            :class="{ 'text-blue-600 font-semibold': $route.path === '/admin/change-password' }"
          >
            Change Password
          </router-link>
        </nav>
      </div>
      <button @click="logout" class="btn btn-ghost text-black">Logout</button>
    </div>

    <!-- Change Password Form -->
    <div class="max-w-md mx-auto mt-10">
      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-semibold mb-4 text-black">Change Password</h2>
        <form @submit.prevent="handleChangePassword" class="space-y-4">
          <div class="form-control">
            <label class="label">
              <span class="label-text text-black">Current Password</span>
            </label>
            <input 
              type="password" 
              v-model="currentPassword"
              class="input input-bordered text-black"
              required
            />
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text text-black">New Password</span>
            </label>
            <input 
              type="password" 
              v-model="newPassword"
              class="input input-bordered text-black"
              required
              @input="validatePassword"
            />
            <!-- Password Requirements -->
            <div class="mt-2 text-sm space-y-1">
              <div :class="{'text-green-600': hasMinLength, 'text-red-600': !hasMinLength}">
                ✓ At least 8 characters
              </div>
              <div :class="{'text-green-600': hasUpperCase, 'text-red-600': !hasUpperCase}">
                ✓ At least one uppercase letter
              </div>
              <div :class="{'text-green-600': hasLowerCase, 'text-red-600': !hasLowerCase}">
                ✓ At least one lowercase letter
              </div>
              <div :class="{'text-green-600': hasNumber, 'text-red-600': !hasNumber}">
                ✓ At least one number
              </div>
              <div :class="{'text-green-600': hasSpecialChar, 'text-red-600': !hasSpecialChar}">
                ✓ At least one special character (!@#$%^&*)
              </div>
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text text-black">Confirm New Password</span>
            </label>
            <input 
              type="password" 
              v-model="confirmPassword"
              class="input input-bordered text-black"
              required
            />
            <div v-if="confirmPassword && !passwordsMatch" class="text-red-600 text-sm mt-1">
              Passwords do not match
            </div>
          </div>

          <button 
            type="submit" 
            class="btn btn-primary w-full"
            :disabled="isLoading || !isValid"
          >
            <span v-if="isLoading" class="text-white">
              <span class="loading loading-spinner loading-sm"></span>
              Updating...
            </span>
            <span v-else class="text-white">Update Password</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useEnv } from '../composables/useEnv'

const router = useRouter()
const logout = () => {
  localStorage.removeItem('adminToken')
  router.push('/admin/login')
}

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const isLoading = ref(false)

// Password validation flags
const hasMinLength = ref(false)
const hasUpperCase = ref(false)
const hasLowerCase = ref(false)
const hasNumber = ref(false)
const hasSpecialChar = ref(false)

// Validate password as user types
const validatePassword = () => {
  const password = newPassword.value
  hasMinLength.value = password.length >= 8
  hasUpperCase.value = /[A-Z]/.test(password)
  hasLowerCase.value = /[a-z]/.test(password)
  hasNumber.value = /[0-9]/.test(password)
  hasSpecialChar.value = /[!@#$%^&*]/.test(password)
}

// Check if passwords match
const passwordsMatch = computed(() => {
  return newPassword.value === confirmPassword.value
})

// Check if password meets all requirements
const isPasswordValid = computed(() => {
  return hasMinLength.value && 
         hasUpperCase.value && 
         hasLowerCase.value && 
         hasNumber.value && 
         hasSpecialChar.value
})

// Overall form validation
const isValid = computed(() => {
  return currentPassword.value && 
         newPassword.value && 
         confirmPassword.value && 
         passwordsMatch.value &&
         isPasswordValid.value
})

const { apiUrl } = useEnv()

const handleChangePassword = async () => {
  if (!isPasswordValid.value) {
    alert('Please ensure your password meets all requirements')
    return
  }

  if (!passwordsMatch.value) {
    alert('New passwords do not match')
    return
  }

  isLoading.value = true
  try {
    const token = localStorage.getItem('adminToken')
    const response = await fetch(`${apiUrl}/api/admin/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        currentPassword: currentPassword.value,
        newPassword: newPassword.value
      })
    })

    const data = await response.json()

    if (response.ok) {
      alert('Password updated successfully')
      currentPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''
      // Reset validation flags
      validatePassword()
    } else {
      alert(data.message || 'Failed to update password')
    }
  } catch (error) {
    console.error('Error updating password:', error)
    alert('Failed to update password')
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.input {
  background-color: white !important;
  border: 1px solid #e5e7eb !important;
}

.input:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 1px #3b82f6 !important;
}

/* Force black text */
.text-black,
.input,
.label-text {
  color: black !important;
}

/* Remove any unwanted margins/padding */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* Style for input placeholder */
.input::placeholder {
  color: #6b7280 !important;
}
</style> 